import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { GetStaticPaths, GetStaticProps } from "next";
import Image from "next/image";
import useIntersectionObserver from "@react-hook/intersection-observer";
import qs from "query-string";
import PromotionsService from "domain/promotions/promotionsService";
import Head from "lib/components/Head";
import useScript from "lib/hooks/useScript";
import { useEvent } from "lib/contexts/eventContext";
import useIsFromTrackerApp from "lib/hooks/useIsFromTrackerApp";

interface PromotionPageProps {
  data?: ResPromotionPage;
}

const PromotionPage: React.FC<PromotionPageProps> = ({ data }) => {
  const isFromTrackerApp = useIsFromTrackerApp();

  if (!data) return null;

  return (
    <div
      css={`
        background-color: ${data.bgColor};
      `}
    >
      <Head
        title={`${data.title} | Lawfully`}
        desc={data.description}
        url={"/promotions/" + data.slug}
        imageUrl={data.ogImageSrc}
      />
      <style>{`html { background-color: ${data.bgColor}; }`}</style>
      <div
        css={`
          margin: 0 auto;
          max-width: 428px;
        `}
      >
        {data.contents.map((section) => (
          <Section
            key={section._key}
            section={section}
            fromTrackerApp={isFromTrackerApp}
          />
        ))}
      </div>
    </div>
  );
};

export default PromotionPage;

interface SectionProps {
  section: ResPromotionPageSection;
  fromTrackerApp: boolean;
}

const Section: React.FC<SectionProps> = ({ section, fromTrackerApp }) => {
  const { track } = useEvent();
  const { _key, _type, bgColor, width, height, src, link, events } = section;
  /**
   * `href` attribute for link (if any). If embeded in tracker app,
   * pass additional event tracking data via query params.
   */
  const href = useMemo(() => {
    if (!link) return "";
    if (fromTrackerApp && events?.app) {
      const { event, properties = [] } = events.app;
      const urlParsed = qs.parseUrl(link);
      const dataEvent = {
        name: event,
        properties: properties.reduce(
          (acc, property) => ({ ...acc, [property.key]: property.value }),
          {},
        ),
      };
      const queryMerged = {
        ...urlParsed.query,
        event: JSON.stringify(dataEvent),
      };
      return qs.stringifyUrl({ ...urlParsed, query: queryMerged });
    }
    return link;
  }, [link, fromTrackerApp, events?.app]);

  const handleOnClick = useCallback(() => {
    if (!fromTrackerApp && events?.web?.event) {
      const properties = events.web.properties.reduce(
        (acc, property) => ({ ...acc, [property.key]: property.value }),
        {},
      );
      track(events.web.event, properties);
    }
  }, [events?.web, fromTrackerApp, track]);

  const Comp = useMemo(() => {
    if (_type === "riveSection") {
      return <SectionRive src={src} width={width} height={height} id={_key} />;
    }
    if (_type === "imageSection") {
      return (
        <SectionImage
          src={src}
          width={width}
          height={height}
          bgColor={bgColor}
        />
      );
    }
    return null;
  }, [_key, _type, bgColor, height, src, width]);

  if (href) {
    return (
      <a href={href} onClick={handleOnClick}>
        {Comp}
      </a>
    );
  }
  return Comp;
};

interface SectionRiveProps {
  src: string;
  width: number;
  height: number;
  id: string;
}

const SectionRive: React.FC<SectionRiveProps> = ({
  src,
  width,
  height,
  id,
}) => {
  const status = useScript("https://unpkg.com/rive-js@0.7.11/dist/rive.min.js");
  const isRiveReady = useMemo(() => status === "ready", [status]);
  const container = useRef<HTMLDivElement>(null);
  const [isAnimPlaying, setIsAnimPlaying] = useState(false);
  const { isIntersecting } = useIntersectionObserver(container, {
    threshold: 0.1,
  });

  useEffect(() => {
    if (isRiveReady) {
      setIsAnimPlaying(isIntersecting);
    }
  }, [isRiveReady, isIntersecting, src]);

  return (
    <div
      css={`
        display: flex;
        width: 100%;
        max-width: 428px;
      `}
      ref={container}
    >
      <div
        css={`
          width: 100%;
          height: 0;
          padding-bottom: ${(height / width) * 100}%;
        `}
      >
        {isAnimPlaying && (
          <RiveCanvas
            id={`riveCanvas${id}`}
            width={2 * width}
            height={2 * height}
            src={src}
          />
        )}
      </div>
    </div>
  );
};

interface RiveCanvasProps {
  id: string;
  width: number;
  height: number;
  src: any;
}

const RiveCanvas: React.FC<RiveCanvasProps> = ({ id, width, height, src }) => {
  useEffect(() => {
    new window.rive.Rive({
      src,
      canvas: document.getElementById(id),
      layout: new window.rive.Layout({ fit: "contain" }),
      autoplay: true,
    });
  }, [id, src]);

  return (
    <canvas style={{ width: "100%" }} id={id} width={width} height={height} />
  );
};

interface SectionImageProps {
  src: string;
  width: number;
  height: number;
  bgColor: string;
}

const SectionImage: React.FC<SectionImageProps> = ({
  src,
  width,
  height,
  bgColor,
}) => {
  return (
    <div
      css={`
        & > div {
          vertical-align: top;
        }
      `}
    >
      <Image
        css={`
          background: ${bgColor};
        `}
        draggable={false}
        priority
        src={src}
        width={width}
        height={height}
      />
    </div>
  );
};

export const getStaticProps: GetStaticProps = async ({ params, preview }) => {
  const { promotionSlug } = params;
  const resourcesService = new PromotionsService();
  const data = await resourcesService.getPromotionPage({
    slug: promotionSlug as string,
    preview: !!preview,
  });

  return {
    props: {
      data: data || null,
    },
    revalidate: 10,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await new PromotionsService().getListPromotionPage();
  const paths = slugs
    .filter((s) => !!s)
    .map((promotionSlug) => ({ params: { promotionSlug } }));

  return {
    paths,
    fallback: "blocking",
  };
};
